import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          throw new Error('No session_id in URL');
        }

        const sessionId = sessionIdMatch[1];
        
        // Check localStorage for HRMS redirect path to determine intended portal
        const hrmsRedirectPath = localStorage.getItem('hrms_redirect_path');
        let intendedPortal = null;
        
        if (hrmsRedirectPath) {
          if (hrmsRedirectPath.includes('/hrms/hr')) {
            intendedPortal = 'hr';
          } else if (hrmsRedirectPath.includes('/hrms/recruiter')) {
            intendedPortal = 'recruiter';
          } else if (hrmsRedirectPath.includes('/hrms/employee')) {
            intendedPortal = 'employee';
          }
        }

        // Exchange session_id for session_token
        const response = await axios.post(
          `${API_URL}/api/auth/session`,
          { session_id: sessionId, intended_portal: intendedPortal },
          { withCredentials: true }
        );

        const user = response.data.user;
        
        toast.success('Login successful!');
        
        // Determine redirect path
        let redirectPath = '/dashboard';
        
        // Check for intended redirect from admin/teacher/student/parent login
        const intendedRedirect = localStorage.getItem('intended_redirect_path');
        const storedIntendedPortal = localStorage.getItem('intended_portal');
        if (intendedRedirect) {
          localStorage.removeItem('intended_redirect_path');
          localStorage.removeItem('intended_portal');
          redirectPath = intendedRedirect;
        } else if (hrmsRedirectPath) {
          // Clear the stored path after reading
          localStorage.removeItem('hrms_redirect_path');
          redirectPath = hrmsRedirectPath;
        } else if (storedIntendedPortal === 'student') {
          localStorage.removeItem('intended_portal');
          redirectPath = '/student';
        } else if (storedIntendedPortal === 'parent') {
          localStorage.removeItem('intended_portal');
          redirectPath = '/dashboard';
        } else {
          // Fallback: Check current pathname for HRMS paths
          const currentPath = location.pathname;
          if (currentPath.startsWith('/hrms/hr')) {
            redirectPath = '/hrms/hr';
          } else if (currentPath.startsWith('/hrms/employee')) {
            redirectPath = '/hrms/employee';
          } else if (currentPath.startsWith('/hrms/recruiter')) {
            redirectPath = '/hrms/recruiter';
          }
        }
        
        // Navigate to appropriate dashboard with user data
        navigate(redirectPath, { state: { user }, replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        const detail = error.response?.data?.detail;
        // iter 313 — parent-not-registered redirect for legacy Emergent flow.
        const isParentNotReg = (typeof detail === 'object' && detail?.code === 'parent_not_registered');
        const errorMessage = isParentNotReg
          ? detail.message
          : (typeof detail === 'string' ? detail : 'Authentication failed. Please try again.');
        toast.error(errorMessage);
        if (isParentNotReg) {
          const email = detail.email || '';
          navigate(`/login?error=parent_not_registered${email ? `&email=${encodeURIComponent(email)}` : ''}`, { replace: true });
          return;
        }
        
        // If trying to access HRMS but got an auth error, redirect to HRMS login
        const hrmsRedirectPath = localStorage.getItem('hrms_redirect_path');
        if (hrmsRedirectPath && hrmsRedirectPath.includes('/hrms/')) {
          localStorage.removeItem('hrms_redirect_path');
          const portal = hrmsRedirectPath.includes('/hrms/hr') ? 'hr' 
            : hrmsRedirectPath.includes('/hrms/recruiter') ? 'recruiter' 
            : 'employee';
          navigate(`/hrms/login?portal=${portal}`, { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      }
    };

    processSession();
  }, [location.hash, location.pathname, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-950">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-300">Authenticating...</p>
      </div>
    </div>
  );
}

export default AuthCallback;
